<template>
  <div class="fin-panel-body">
    <dl class="fin-kv">
      <dt>{{ $t("restaurant") }}</dt><dd>{{ restaurantText }}</dd>
      <dt>{{ $t("date") }}</dt><dd>{{ row.date ? localeDate(row.date) : "—" }}</dd>
      <dt>{{ $t("finance_posting.col_outcome") }}</dt><dd>{{ row.outcome ? outcomeText(row.outcome) : "—" }}</dd>
      <dt>{{ $t("finance_posting.col_payment") }}</dt><dd>{{ paymentText || "—" }}</dd>
      <template v-if="row.courier"><dt>{{ $t("finance_posting.col_courier") }}</dt><dd>{{ toLatin(row.courier) }}</dd></template>
      <dt>{{ $t("finance_posting.col_value") }}</dt><dd class="fin-num">{{ row.value === null ? "—" : money(row.value) }}</dd>
      <template v-if="row.finalAt"><dt>{{ $t("finance_posting.panel_since") }}</dt><dd>{{ localeDate(row.finalAt) }}</dd></template>
    </dl>

    <ui-state v-if="status === 'loading'" kind="loading" />
    <ui-banner v-else-if="status === 'error'" tone="warn" role="alert">{{ $t("finance_posting.panel_error") }}</ui-banner>
    <template v-else>
      <ui-banner v-if="check.posted" tone="info" icon="mdi-check-circle-outline">{{ $t("finance_posting.panel_posted") }}</ui-banner>
      <ui-banner v-else-if="!check.canPost" tone="warn">
        {{ $t("finance_posting.panel_cannot") }}<template v-if="check.reason"> {{ translate(check.reason) }}</template>
      </ui-banner>
      <ui-banner v-else tone="info">{{ $t("finance_posting.panel_can") }}</ui-banner>

      <ui-banner v-if="result" :tone="result.status === 'posted' ? 'info' : 'warn'">{{ resultLine }}</ui-banner>

      <section v-if="check.transactions.length">
        <h4 class="fin-h4">{{ $t("finance_posting.panel_transactions") }}</h4>
        <div class="fin-lines-box">
          <div v-for="(transaction, index) in check.transactions" :key="transaction.id || index" class="fin-lines-box__row fin-lines-box__row--stack">
            <span>{{ transaction.entryTypes.map(typeText).join(", ") || "—" }}</span>
            <small class="ui-muted">{{ transactionMeta(transaction) }}</small>
          </div>
        </div>
      </section>
      <p v-else-if="check.posted" class="ui-muted">{{ $t("finance_posting.panel_none") }}</p>

      <ui-button v-if="!check.posted && check.canPost" variant="primary" block icon="mdi-check" :disabled="busy" @click="post">{{ $t("finance_posting.post") }}</ui-button>
    </template>
  </div>
</template>

<script>
import ledgerAdminService from "@/services/ledgerAdmin";
import { formatMoney } from "@/components/restaurant/finance/constants";
import { mapOrderCheck } from "../postingLogic";

/**
 * Stanje jedne narudžbe u knjizi (GET /admin/ledger/orders/{id}): da li je proknjižena, može li se proknjižiti (i zašto ne),
 * njena knjiženja i dugme "Proknjiži". Knjiženje radi ljuska (postOne), pa se red i lista osvježavaju na jednom mjestu.
 */
export default {
  name: "PostingOrderCheckPanel",
  inject: { shell: "reportShell" },
  props: { row: { type: Object, required: true } },
  data: () => ({ status: "loading", check: { posted: false, canPost: false, reason: "", transactions: [] }, result: null }),
  computed: {
    busy() {
      return Boolean(this.shell.busy[this.row.key]) || this.shell.bulkBusy;
    },
    restaurantText() {
      return this.shell.restaurantText(this.row);
    },
    paymentText() {
      return this.shell.paymentText(this.row.paymentMethod);
    },
    resultLine() {
      return this.result ? this.shell.resultLine(this.result) : "";
    },
  },
  created() {
    this.load();
  },
  methods: {
    money(value, signed) {
      return formatMoney(value, this.getCurrencyByRestaurantId(this.row.restaurantId) || "KM", { signed });
    },
    outcomeText(outcome) {
      return this.shell.outcomeText(outcome);
    },
    typeText(key) {
      const path = `finance_shell.types.${key}`;
      return this.$te(path) ? this.$t(path) : String(key || "").toLowerCase().replace(/_/g, " ");
    },
    /** vrijeme, ko je knjižio i koliko je stavki u knjiženju (backend ne šalje iznos po knjiženju) */
    transactionMeta(transaction) {
      const parts = [];
      if (transaction.date) {
        parts.push(this.localeDate(transaction.date, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }));
      }
      if (transaction.createdBy) {
        const name = transaction.createdBy === "system" ? this.$t("finance_posting.panel_system") : this.translate(transaction.createdBy);
        parts.push(this.$t("finance_posting.panel_by", { name }));
      }
      if (transaction.lines !== null) {
        parts.push(this.$t("finance_posting.panel_lines", { count: transaction.lines }));
      }
      return parts.join(" · ");
    },
    async load() {
      this.status = "loading";
      try {
        const { data } = await ledgerAdminService.getOrder(this.row.orderId);
        this.check = mapOrderCheck(data);
        this.status = "ready";
      } catch (error) {
        this.status = "error";
      }
    },
    async post() {
      const result = await this.shell.confirmPost(this.row);
      if (!result) return;
      this.result = result;
      this.load();
    },
  },
};
</script>
